import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Button, Modal, ModalBody, ModalHeader, Row, Col } from "reactstrap";
import {
    LinearProgress, Tooltip, Checkbox,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    Paper, TextField, InputAdornment
} from "@mui/material";
import Badge from "react-bootstrap/Badge";
import PndRestApi from "src/utils/pnd/PndRestApi";
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import renderSelectField from "src/shared/components/form/Select";
import { showNotification, successNofication } from "src/shared/components/Notification";
import HubIcon from '@mui/icons-material/OtherHouses';
import ConfirmationDialog from "src/shared/components/ConfirmationDialog";
import { ERROR_MESSAGES } from "src/utils/pnd/PndConstants";
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';

const required = value => value ? undefined : 'Required';

const DriverHubAssignModal = ({ modalShow, setModalShow, isTitle, setRedirect }) => {
    useEffect(() => {
        getAllVehicleDetails();
        GetAllActiveHub();
    }, []);

    const [isSelectedHubId, setSelectedHubId] = useState(null);
    const [isActiveHubList, setActiveHubList] = useState([]);
    const [isUnassignData, setUnassignData] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [selectedDriverIds, setSelectedDriverIds] = useState([]);
    const [reactTableData, setReactTableData] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [selectedHubName, setSelectedHubName] = useState("");

    // Pagination & Search State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchText, setSearchText] = useState("");

    const getAllVehicleDetails = async () => {
        try {
            setLoading(true);
            const response = await PndRestApi.GetAllAssignedDriverDetails(-1);
            if (response.status === 200 && response.data.status === 200) {
                // We don't need 'checkbox' field in data for MUI table, we track via selectedDriverIds
                setReactTableData(response.data.data);
            } else {
                setReactTableData([]);
                showNotification("theme-light", "ltr", `SuccessCode: ${response.data.status}`, `Message:${response.data.message}`);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification("theme-light", "ltr", "Error", "Error fetching data");
        } finally {
            setLoading(false);
        }
    }

    const GetAllActiveHub = async () => {
        try {
            const response = await PndRestApi.GetAllActiveHub();
            if (response?.status === 200 && response?.data?.status === 200) {
                if (response.data.data.length > 0) {
                    const activeHub = response.data.data.map(item => ({ "label": item.hubName, "value": item.hubId }));
                    setActiveHubList(activeHub);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const toggle = () => {
        setModalShow(prevState => !prevState)
    }

    // --- Table Logic ---

    const handleCheckboxClick = (event, driverId) => {
        event.stopPropagation();
        const selectedIndex = selectedDriverIds.indexOf(driverId);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedDriverIds, driverId);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedDriverIds.slice(1));
        } else if (selectedIndex === selectedDriverIds.length - 1) {
            newSelected = newSelected.concat(selectedDriverIds.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedDriverIds.slice(0, selectedIndex),
                selectedDriverIds.slice(selectedIndex + 1),
            );
        }
        setSelectedDriverIds(newSelected);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            // Select all ASSIGNABLE drivers
            const newSelecteds = filteredData
                .filter(n => (n.hubId === null || !n.riderHubMappingStatus))
                .map((n) => n.driverId);
            setSelectedDriverIds(newSelecteds);
            return;
        }
        setSelectedDriverIds([]);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event) => {
        setSearchText(event.target.value);
        setPage(0);
    };

    // Filter data based on search
    const filteredData = reactTableData.filter((row) => {
        if (!searchText) return true;
        const searchLower = searchText.toLowerCase();
        return (
            (row.driverName && row.driverName.toLowerCase().includes(searchLower)) ||
            (row.userName && row.userName.toLowerCase().includes(searchLower))
        );
    });

    // Pagination logic
    const visibleRows = filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
    );

    // Calculate indeterminate state for header checkbox
    // Only count assignable drivers for "Select All" logic
    const assignableCount = filteredData.filter(d => (d.hubId === null || !d.riderHubMappingStatus)).length;
    const isSelectedCount = selectedDriverIds.length;
    const isAllSelected = assignableCount > 0 && isSelectedCount === assignableCount;
    const isIndeterminate = isSelectedCount > 0 && isSelectedCount < assignableCount;

    // --- Action Logic ---

    const handleUnassign = (rowData) => {
        setUnassignData(rowData);
        setShowConfirmation(true);
    };

    const handleConfirmUnassign = async () => {
        try {
            let obj = {
                "hubId": isUnassignData.hubId,
                "driverId": isUnassignData.riderId
            };
            const response = await PndRestApi.UnAssignDriverFromHub(obj);
            if (response.status === 200 && response.data.status === 200) {
                successNofication("theme-light", "ltr", `Message: Rider Unassigned from the Hub`);
                getAllVehicleDetails();
                setShowConfirmation(false);
            } else {
                showNotification("theme-light", "ltr", "Error", "Operation Failed");
            }
        } catch (error) {
            showNotification("theme-light", "ltr", "Error", "Operation Failed");
        } finally {
            setShowConfirmation(false);
        }
    };

    const handleCancelUnassign = () => setShowConfirmation(false);

    const handleSubmitAssign = async () => {
        try {
            if (!isSelectedHubId) {
                showNotification("theme-light", "ltr", "Error", "Please select a Hub first.");
                return;
            }
            if (!selectedDriverIds || selectedDriverIds.length === 0) {
                showNotification("theme-light", "ltr", "Error", "Please select at least one driver.");
                return;
            }

            let obj = {
                "hubId": isSelectedHubId,
                "driverId": selectedDriverIds
            };

            const response = await PndRestApi.AssignDriverToHub(obj);
            if (response.status === 200 && response.data.status === 200) {
                successNofication("theme-light", "ltr", `SuccessCode: ${response.data.status}`, ERROR_MESSAGES.successMessage);
                setModalShow(false);
                if (setRedirect) setRedirect(true);
            } else {
                showNotification("theme-light", "ltr", `SuccessCode: ${response.data.status}`, `Message:${response.data.message}`);
            }
        } catch (error) {
            console.error('Error in handleSubmitAssign:', error);
            showNotification("theme-light", "ltr", "Error", ERROR_MESSAGES.tryCatchError);
        }
    };

    const handleHubChange = (event) => {
        if (event) {
            setSelectedHubId(event.value);
            setSelectedHubName(event.label);
        } else {
            setSelectedHubId(null);
            setSelectedHubName("");
        }
    };

    return (
        <Modal
            isOpen={modalShow}
            toggle={toggle}
            size="xl"
            centered
            className="theme-light"
            contentClassName="border-0 overflow-hidden"
        >
            <ModalHeader toggle={toggle} className="border-bottom bg-white py-3">
                <div className="d-flex align-items-center">
                    <div className="bg-primary bg-opacity-10 text-primary rounded px-3 py-2 me-3">
                        <AssignmentTurnedInIcon />
                    </div>
                    <div>
                        <h5 className="mb-0 fw-bold">Driver Assignment</h5>
                        <small className="text-muted">Allocating drivers to hubs</small>
                    </div>
                </div>
            </ModalHeader>
            <ModalBody className="p-0">
                {isLoading && <LinearProgress color="primary" style={{ position: 'absolute', width: '100%', top: 0, zIndex: 10 }} />}

                <Row className="g-0" style={{ minHeight: '600px' }}>
                    {/* Left Sidebar: Control Panel */}
                    <Col md={3} className="bg-light border-end p-4 d-flex flex-column">
                        <div className="mb-4">
                            <h6 className="fw-bold text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>1. Select Destination</h6>
                            <div className="mb-3">
                                <label className="form-label fw-bold">Target Hub</label>
                                <Field
                                    validate={[required]}
                                    name="hub"
                                    component={renderSelectField}
                                    type="text"
                                    options={isActiveHubList}
                                    placeholder="Select Hub"
                                    onChange={handleHubChange}
                                    isClearable={true}
                                />
                            </div>
                            {selectedHubName && (
                                <div className="alert alert-info p-2 mt-2 py-2" role="alert">
                                    <small><HubIcon fontSize="small" className="me-1 mb-1" /> Target: <strong>{selectedHubName}</strong></small>
                                </div>
                            )}
                        </div>

                        <div className="mb-auto">
                            <h6 className="fw-bold text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>2. Assignment Summary</h6>
                            <div className="card border-0 shadow-sm">
                                <div className="card-body text-center">
                                    <h2 className="display-4 fw-bold text-primary mb-0">{selectedDriverIds.length}</h2>
                                    <p className="text-muted mb-0 small">Drivers Selected</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 pt-3 border-top">
                            <Button
                                color="primary"
                                size="lg"
                                className="w-100 shadow-sm"
                                onClick={handleSubmitAssign}
                                disabled={!isSelectedHubId || selectedDriverIds.length === 0}
                            >
                                Confirm Assignment
                            </Button>
                            <Button color="link" className="w-100 mt-2 text-muted text-decoration-none" onClick={toggle}>
                                Cancel
                            </Button>
                        </div>
                    </Col>

                    {/* Right Content: MUI Table */}
                    <Col md={9} className="bg-white p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold text-uppercase text-muted mb-0" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>Available Drivers</h6>
                            <TextField
                                size="small"
                                placeholder="Search driver..."
                                variant="outlined"
                                value={searchText}
                                onChange={handleSearch}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" className="text-muted" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: '250px' }}
                            />
                        </div>

                        <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none', border: '1px solid #eee' }}>
                            <TableContainer sx={{ maxHeight: 500 }}>
                                <Table stickyHeader aria-label="sticky table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox" align="center" style={{ width: '60px' }}>
                                                <Checkbox
                                                    color="primary"
                                                    indeterminate={isIndeterminate}
                                                    checked={isAllSelected}
                                                    onChange={handleSelectAllClick}
                                                    inputProps={{
                                                        'aria-label': 'select all desserts',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>Driver Name</TableCell>
                                            <TableCell>Assigned Hub</TableCell>
                                            <TableCell align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {visibleRows.map((row) => {
                                            const isSelected = selectedDriverIds.indexOf(row.driverId) !== -1;
                                            const isAssignable = (row.hubId === null || !row.riderHubMappingStatus);
                                            const labelId = `enhanced-table-checkbox-${row.driverId}`;

                                            return (
                                                <TableRow
                                                    hover
                                                    onClick={(event) => {
                                                        if (isAssignable) handleCheckboxClick(event, row.driverId);
                                                    }}
                                                    role="checkbox"
                                                    aria-checked={isSelected}
                                                    tabIndex={-1}
                                                    key={row.driverId}
                                                    selected={isSelected}
                                                    sx={{ cursor: isAssignable ? 'pointer' : 'default', opacity: isAssignable ? 1 : 0.7 }}
                                                >
                                                    <TableCell padding="checkbox" align="center">
                                                        {isAssignable ? (
                                                            <Checkbox
                                                                color="primary"
                                                                checked={isSelected}
                                                                inputProps={{
                                                                    'aria-labelledby': labelId,
                                                                }}
                                                            />
                                                        ) : (
                                                            <Tooltip title="Already Assigned">
                                                                <span><Checkbox disabled checked={false} /></span>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        component="th"
                                                        id={labelId}
                                                        scope="row"
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <AccountCircleIcon className="text-muted me-2" style={{ fontSize: '2rem' }} />
                                                            <div className="d-flex flex-column">
                                                                <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{row.driverName}</span>
                                                                <span style={{ fontSize: '0.8rem', color: '#888' }}>{row.userName}</span>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.hubName ? (
                                                            <Badge bg="info" className="p-2 fw-normal" style={{ fontSize: '0.85em', backgroundColor: '#e1f5fe', color: '#0288d1' }}>
                                                                <HubIcon style={{ fontSize: '14px', marginRight: '4px', verticalAlign: 'text-bottom' }} />
                                                                {row.hubName}
                                                            </Badge>
                                                        ) : (
                                                            <Badge bg="light" text="dark" className="p-2 fw-normal border">
                                                                Unassigned
                                                            </Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {row.riderHubMappingStatus && (
                                                            <Tooltip title="Unassign from Hub">
                                                                <span>
                                                                    <Button
                                                                        color="danger"
                                                                        outline
                                                                        size="sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleUnassign(row);
                                                                        }}
                                                                        className="btn-icon"
                                                                        style={{ padding: '4px 8px' }}
                                                                    >
                                                                        <RemoveCircleOutlineIcon style={{ fontSize: '18px' }} />
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {visibleRows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" style={{ height: 200 }}>
                                                    <span className="text-muted">No drivers found.</span>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={filteredData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </Col>
                </Row>
            </ModalBody>

            {showConfirmation && (
                <ConfirmationDialog
                    open={showConfirmation}
                    onClose={handleCancelUnassign}
                    onConfirm={handleConfirmUnassign}
                    title="Confirm Unassignment"
                    message={`Are you sure you want to unassign ${isUnassignData?.driverName}?`}
                />
            )}
        </Modal>
    );
};

DriverHubAssignModal.propTypes = {
    modalShow: PropTypes.bool.isRequired,
    setModalShow: PropTypes.func.isRequired,
    isTitle: PropTypes.string,
    setRedirect: PropTypes.func
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps)(reduxForm({
    form: 'VehicleHubAssignmentForm',
    enableReinitialize: true
})(DriverHubAssignModal));
